import type { Question } from '../types'
import { createMathVariantPack } from './math_authored_variants'
import { getSmartQuestionId } from '../lib/smartQuestionId'

export const TSA_MATH_PILOT = { questions: 40, minutes: 60 } as const

export type TsaCoverageKey = 'sequences-finance' | 'functions-calculus' | 'probability-counting' | 'geometry-spatial'
type TsaCoverageBucket = { key: TsaCoverageKey; label: string; matches: (question: Question) => boolean }

const contains = (question: Question, values: string[]) => {
  const text = `${question.topic} ${question.tags.join(' ')}`.toLocaleLowerCase('vi')
  return values.some(value => text.includes(value))
}

/** Internal balance rules for SangMath's pilot—not a claim of an official TSA quota. */
export const tsaCoverageBuckets: TsaCoverageBucket[] = [
  { key: 'sequences-finance', label: 'Dãy số · tài chính', matches: question => contains(question, ['dãy số', 'cấp số', 'finance', 'trả góp', 'cashflow']) },
  { key: 'functions-calculus', label: 'Hàm số · giải tích', matches: question => contains(question, ['giới hạn', 'logistic', 'đạo hàm', 'tối ưu', 'mũ', 'logarit', 'optimization']) },
  { key: 'probability-counting', label: 'Tổ hợp · xác suất', matches: question => contains(question, ['xác suất', 'bayes', 'tổ hợp', 'counting', 'allocation']) },
  { key: 'geometry-spatial', label: 'Hình học · Oxyz', matches: question => contains(question, ['oxyz', 'vectơ', 'hình học', 'spatial-geometry', 'cylinder']) },
]

export type TsaCoverageSummary = { key: TsaCoverageKey; label: string; count: number }

export function getTsaMathCoverage(questions: Question[]): TsaCoverageSummary[] {
  return tsaCoverageBuckets.map(bucket => ({
    key: bucket.key,
    label: bucket.label,
    count: questions.filter(bucket.matches).length,
  }))
}

const makeSeed = () => typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`

function hash(value: string) {
  let result = 2166136261
  for (const character of value) { result ^= character.charCodeAt(0); result = Math.imul(result, 16777619) }
  return result >>> 0
}

function shuffle<T>(items: T[], seed: string) {
  let state = hash(seed)
  const next = () => { state += 0x6D2B79F5; let value = state; value = Math.imul(value ^ (value >>> 15), value | 1); value ^= value + Math.imul(value ^ (value >>> 7), value | 61); return ((value ^ (value >>> 14)) >>> 0) / 4294967296 }
  const copy = [...items]
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const target = Math.floor(next() * (index + 1)); [copy[index], copy[target]] = [copy[target], copy[index]]
  }
  return copy
}

/**
 * A reproducible 40-item TSA Mathematics pilot. Authored TSA drafts give the
 * paper its breadth; parameterized families give each seed fresh numerical
 * data and prevent learners from exhausting a valid set.
 */
export function createTsaMathPilot(authoredTsa: Question[], seed = makeSeed()): Question[] {
  const shuffledAuthored = shuffle(authoredTsa, `${seed}:authored`)
  const selectedIds = new Set<string>()
  // Select a hard authored anchor from every covered cluster first. This makes
  // each paper diagnostic across the matrix rather than merely random.
  const anchors = tsaCoverageBuckets.flatMap(bucket => {
    const candidate = shuffledAuthored.find(question => !selectedIds.has(question.id) && bucket.matches(question))
    if (candidate) selectedIds.add(candidate.id)
    return candidate ? [candidate] : []
  })
  const authoredTarget = Math.min(16, authoredTsa.length)
  const authored = [...anchors, ...shuffledAuthored.filter(question => !selectedIds.has(question.id)).slice(0, Math.max(0, authoredTarget - anchors.length))]
  const generated = Array.from({ length: 8 }, (_, index) => createMathVariantPack(`${seed}:model:${index + 1}`)
    .map((question, itemIndex): Question => ({
      ...question,
      id: `TSA-GEN-${seed}-${index + 1}-${itemIndex + 1}`,
      exam: 'TSA',
      domain: 'Tư duy Toán học',
      tags: [...question.tags, 'tsa-math-generator', 'parameterized', 'editorial-draft'],
      variantGroup: `tsa-${question.variantGroup}`,
    }))).flat()
  return shuffle([...authored, ...shuffle(generated, `${seed}:generated`).slice(0, TSA_MATH_PILOT.questions - authored.length)], `${seed}:paper`)
}

/** Exports only student-facing data; answer keys and explanations stay out. */
export function downloadTsaMathPilot(questions: Question[], seed: string) {
  const studentQuestions = questions.map(question => {
    const { answer, solution, hint1, hint2, aiContext, ...studentQuestion } = question
    return { ...studentQuestion, contentCode: getSmartQuestionId(question) }
  })
  const payload = {
    title: 'Math ĐGNL — TSA Tư duy Toán học (Pilot)',
    durationMinutes: TSA_MATH_PILOT.minutes,
    seed,
    exportedAt: new Date().toISOString(),
    coverage: getTsaMathCoverage(questions),
    questions: studentQuestions,
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `sangmath-tsa-toan-${seed.slice(0, 8)}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}
